import React from 'react';
import { DayPicker } from 'react-day-picker';
import { format, isValid, getDaysInMonth, parse } from 'date-fns';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const MONTH_LABELS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

// Left-hand calendar: Monday-first, violet selected day, Today button, min/max,
// disabled matcher and optional range shading between start/end.
export function CalendarPanel({
  selected,
  onSelect,
  month,
  onMonthChange,
  minDate,
  maxDate,
  disabledDates,
  rangeStart,
  rangeEnd,
  testIdPrefix = 'tdt',
}) {
  const disabled = [];
  if (minDate) disabled.push({ before: minDate });
  if (maxDate) disabled.push({ after: maxDate });
  if (disabledDates) disabled.push(disabledDates);

  const modifiers = {};
  if (rangeStart && rangeEnd && isValid(rangeStart) && isValid(rangeEnd) && rangeStart < rangeEnd) {
    modifiers.rangeMiddle = { after: rangeStart, before: rangeEnd };
    modifiers.rangeEdge = [rangeStart, rangeEnd];
  }

  // ── Separate Day / Month / Year selectors + typeable field ──────────────────
  const view = month && isValid(month) ? month : new Date();
  const viewYear = view.getFullYear();
  const viewMonth = view.getMonth();
  const daysInView = getDaysInMonth(new Date(viewYear, viewMonth, 1));
  const selDay = selected && isValid(selected)
    && selected.getFullYear() === viewYear && selected.getMonth() === viewMonth
    ? selected.getDate() : '';
  const nowY = new Date().getFullYear();
  const minY = minDate && isValid(minDate) ? minDate.getFullYear() : 1900;
  const maxY = maxDate && isValid(maxDate) ? maxDate.getFullYear() : nowY + 10;
  const years = [];
  for (let y = maxY; y >= minY; y--) years.push(y);

  const applyYearMonth = (y, m) => {
    const base = new Date(y, m, 1);
    onMonthChange(base);
    if (selected && isValid(selected)) {
      const clamped = Math.min(selected.getDate(), getDaysInMonth(base));
      onSelect(new Date(y, m, clamped));
    }
  };
  const onDaySel = (v) => {
    const day = Number(v);
    onMonthChange(new Date(viewYear, viewMonth, 1));
    onSelect(new Date(viewYear, viewMonth, day));
  };

  const [typed, setTyped] = React.useState('');
  React.useEffect(() => {
    setTyped(selected && isValid(selected) ? format(selected, 'dd/MM/yyyy') : '');
  }, [selected]);
  const commitTyped = () => {
    const t = (typed || '').trim();
    if (!t) return;
    const d = parse(t, 'dd/MM/yyyy', new Date());
    if (isValid(d)) { onMonthChange(d); onSelect(d); }
    else { setTyped(selected && isValid(selected) ? format(selected, 'dd/MM/yyyy') : ''); }
  };

  const goToday = () => {
    const t = new Date();
    onMonthChange(t);
    onSelect(t);
  };

  return (
    <div data-testid={`${testIdPrefix}-calendar`}>
      <div className="flex flex-col gap-2 px-1 pb-2">
        <Input
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          onBlur={commitTyped}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitTyped(); } }}
          placeholder="Type date: dd/mm/yyyy"
          className="h-9"
          data-testid={`${testIdPrefix}-typed-input`}
        />
        <div className="grid grid-cols-3 gap-2">
          <Select value={selDay ? String(selDay) : ''} onValueChange={onDaySel}>
            <SelectTrigger className="h-9" data-testid={`${testIdPrefix}-day-select`}><SelectValue placeholder="Day" /></SelectTrigger>
            <SelectContent className="max-h-60">
              {Array.from({ length: daysInView }, (_, i) => i + 1).map((dd) => (
                <SelectItem key={dd} value={String(dd)}>{dd}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(viewMonth)} onValueChange={(v) => applyYearMonth(viewYear, Number(v))}>
            <SelectTrigger className="h-9" data-testid={`${testIdPrefix}-month-select`}><SelectValue placeholder="Month" /></SelectTrigger>
            <SelectContent className="max-h-60">
              {MONTH_LABELS.map((mL, idx) => (
                <SelectItem key={mL} value={String(idx)}>{mL}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(viewYear)} onValueChange={(v) => applyYearMonth(Number(v), viewMonth)}>
            <SelectTrigger className="h-9" data-testid={`${testIdPrefix}-year-select`}><SelectValue placeholder="Year" /></SelectTrigger>
            <SelectContent className="max-h-60">
              {years.map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <DayPicker
        mode="single"
        selected={selected && isValid(selected) ? selected : undefined}
        onSelect={(d) => d && onSelect(d)}
        month={month}
        onMonthChange={onMonthChange}
        weekStartsOn={1}
        showOutsideDays
        fixedWeeks
        disabled={disabled.length ? disabled : undefined}
        modifiers={modifiers}
        modifiersClassNames={{
          rangeMiddle: 'bg-violet-100 !rounded-none',
          rangeEdge: 'bg-violet-700 text-white',
        }}
        formatters={{ formatWeekdayName: (d) => format(d, 'EEE') }}
        classNames={{
          months: '',
          month: 'space-y-2',
          caption: 'flex justify-center pt-1 relative items-center h-8 max-sm:h-10',
          caption_label: 'text-base font-semibold',
          nav: 'flex items-center',
          nav_button:
            'inline-flex items-center justify-center h-8 w-8 max-sm:h-9 max-sm:w-9 rounded-md border border-input bg-white hover:bg-violet-50 transition-colors',
          nav_button_previous: 'absolute left-1',
          nav_button_next: 'absolute right-1',
          table: 'w-full border-collapse',
          head_row: 'flex',
          head_cell: 'text-muted-foreground w-8 max-sm:w-11 font-medium text-xs',
          row: 'flex w-full mt-1',
          cell: 'relative p-0 text-center text-sm',
          day:
            'h-8 w-8 max-sm:h-11 max-sm:w-11 p-0 font-normal rounded-full inline-flex items-center justify-center hover:bg-violet-50 aria-selected:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500',
          day_selected:
            'bg-violet-700 text-white hover:bg-violet-700 hover:text-white focus:bg-violet-700 focus:text-white',
          day_today: 'font-bold text-violet-700',
          day_outside: 'text-muted-foreground/40',
          day_disabled: 'text-muted-foreground opacity-40 line-through',
          day_hidden: 'invisible',
        }}
        components={{
          IconLeft: () => <ChevronLeft className="h-4 w-4" />,
          IconRight: () => <ChevronRight className="h-4 w-4" />,
        }}
      />
      <div className="flex justify-center pb-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="max-sm:h-11 max-sm:px-5"
          onClick={goToday}
          data-testid={`${testIdPrefix}-today`}
        >
          <CalendarDays className="w-4 h-4 mr-2 text-violet-600" />
          Today
        </Button>
      </div>
    </div>
  );
}
